// In src/pages/ChurchManagement.js

import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, onSnapshot, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { uploadFile, deleteFileByUrl } from '../services/storageService';
import { useAuth } from '../context/AuthContext';
// Import Firebase Functions utilities
import { getFunctions, httpsCallable } from 'firebase/functions';

// Import our modal components
import ChurchEditModal from '../components/ChurchEditModal';
import GivingInfoEditModal from '../components/GivingInfoEditModal';

import {
  List, ListItem, ListItemText, Button, Container, Typography,
  CircularProgress, Divider, Paper, Box, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import FolderDeleteIcon from '@mui/icons-material/DeleteForever';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';


function ChurchManagement() {
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userProfile } = useAuth();
  const [givingMethods, setGivingMethods] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [isWipeConfirmOpen, setIsWipeConfirmOpen] = useState(false);
  const [churchToWipe, setChurchToWipe] = useState(null);

  const [isForceReloginConfirmOpen, setIsForceReloginConfirmOpen] = useState(false);
  const [churchToForceRelogin, setChurchToForceRelogin] = useState(null);

  const [isWipeAnnouncementsConfirmOpen, setIsWipeAnnouncementsConfirmOpen] = useState(false);
  const [churchToWipeAnnouncements, setChurchToWipeAnnouncements] = useState(null);

  const [isWipeNotificationsConfirmOpen, setIsWipeNotificationsConfirmOpen] = useState(false);
  const [churchToWipeNotifications, setChurchToWipeNotifications] = useState(null);

  const [isGivingInfoModalOpen, setIsGivingInfoModalOpen] = useState(false);


  const isSuperAdmin = userProfile?.role === 'Super Administrator';
  const isChurchAdmin = userProfile?.role === 'Church Administrator';

  useEffect(() => {
    let unsubscribe;
    setLoading(true);

    if (!userProfile) {
      setLoading(false);
      return;
    }

    if (isSuperAdmin) {
      const churchesColRef = collection(db, 'churches');
      unsubscribe = onSnapshot(churchesColRef, snapshot => {
        setChurches(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }, (error) => {
        console.error("Error fetching churches for Super Admin:", error);
        setLoading(false);
      });
    } else if (isChurchAdmin && userProfile?.churchId) {
      const churchDocRef = doc(db, 'churches', userProfile.churchId);
      unsubscribe = onSnapshot(churchDocRef, docSnap => {
        if (docSnap.exists()) {
          setChurches([{ id: docSnap.id, ...docSnap.data() }]);
        } else {
          setChurches([]);
        }
        setLoading(false);
      }, (error) => {
        console.error("Error fetching church for Church Admin:", error);
        setLoading(false);
      });
    } else {
      setChurches([]);
      setLoading(false);
      return;
    }

    return () => unsubscribe();
  }, [userProfile, isSuperAdmin, isChurchAdmin]);
  
  useEffect(() => {
    if (isGivingInfoModalOpen && selectedChurch?.id) {
      const givingMethodsRef = collection(db, 'churches', selectedChurch.id, 'givingInfo');
      const unsubscribeGiving = onSnapshot(givingMethodsRef, (snapshot) => {
        setGivingMethods(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
      return () => unsubscribeGiving();
    }
  }, [isGivingInfoModalOpen, selectedChurch]);


  const handleCreateChurch = () => {
    if (!isSuperAdmin) return;
    setSelectedChurch(null);
    setIsModalOpen(true);
  };

  const handleEditChurch = (church) => {
    setSelectedChurch(church);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedChurch(null);
  };

  const handleDeleteRequest = (church) => {
    if (!isSuperAdmin) return;
    setSelectedChurch(church);
    setIsConfirmOpen(true);
  };

  const handleOpenGivingInfoModal = (church) => {
    setSelectedChurch(church);
    setIsGivingInfoModalOpen(true);
  };

  const handleCloseGivingInfoModal = () => {
    setIsGivingInfoModalOpen(false);
    setSelectedChurch(null);
  };

  const handleWipeTokensRequest = (church) => {
    if (!isSuperAdmin) return;
    setChurchToWipe(church);
    setIsWipeConfirmOpen(true);
  };

  const handleConfirmWipeTokens = async () => {
    if (!churchToWipe) return;
    setLoading(true);

    try {
      const functions = getFunctions();
      const wipeFcmTokensForChurch = httpsCallable(functions, 'wipeFcmTokensForChurch');
      const result = await wipeFcmTokensForChurch({ churchId: churchToWipe.id });
      alert(result.data.message);
    } catch (error) {
      console.error("Error wiping tokens:", error);
      alert(`Failed to wipe tokens: ${error.message}`);
    } finally {
      setLoading(false);
      setIsWipeConfirmOpen(false);
      setChurchToWipe(null);
    }
  };

  const handleForceReloginRequest = (church) => {
    if (!isSuperAdmin) return;
    setChurchToForceRelogin(church);
    setIsForceReloginConfirmOpen(true);
  };

  const handleConfirmForceRelogin = async () => {
    if (!churchToForceRelogin) return;
    setLoading(true);

    try {
      const functions = getFunctions();
      const revokeUserSessionsForChurch = httpsCallable(functions, 'revokeUserSessionsForChurch');
      const result = await revokeUserSessionsForChurch({ churchId: churchToForceRelogin.id });
      alert(result.data.message);
    } catch (error) {
      console.error("Error forcing re-login:", error);
      alert(`Failed to force re-login: ${error.message}`);
    } finally {
      setLoading(false);
      setIsForceReloginConfirmOpen(false);
      setChurchToForceRelogin(null);
    }
  };

  const handleWipeAnnouncementsRequest = (church) => {
    if (!isSuperAdmin) return;
    setChurchToWipeAnnouncements(church);
    setIsWipeAnnouncementsConfirmOpen(true);
  };

  const handleConfirmWipeAnnouncements = async () => {
    if (!churchToWipeAnnouncements) return;
    setLoading(true);

    try {
      const functions = getFunctions();
      const wipeAnnouncementsForChurch = httpsCallable(functions, 'wipeAnnouncementsForChurch');
      const result = await wipeAnnouncementsForChurch({ churchId: churchToWipeAnnouncements.id });
      alert(result.data.message);
    } catch (error) {
      console.error("Error wiping announcements:", error);
      alert(`Failed to wipe announcements: ${error.message}`);
    } finally {
      setLoading(false);
      setIsWipeAnnouncementsConfirmOpen(false);
      setChurchToWipeAnnouncements(null);
    }
  };

  const handleWipeNotificationsRequest = (church) => {
    if (!isSuperAdmin) return;
    setChurchToWipeNotifications(church);
    setIsWipeNotificationsConfirmOpen(true);
  };

  const handleConfirmWipeNotifications = async () => {
    if (!churchToWipeNotifications) return;
    setLoading(true);

    try {
      const functions = getFunctions();
      const wipeUserNotificationsForChurch = httpsCallable(functions, 'wipeUserNotificationsForChurch');
      const result = await wipeUserNotificationsForChurch({ churchId: churchToWipeNotifications.id });
      alert(result.data.message);
    } catch (error) {
      console.error("Error wiping user notifications:", error);
      alert(`Failed to wipe user notifications: ${error.message}`);
    } finally {
      setLoading(false);
      setIsWipeNotificationsConfirmOpen(false);
      setChurchToWipeNotifications(null);
    }
  };


  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
  };
  
  // --- MODIFIED: This function now handles create and update separately ---
  const handleSaveChurch = async (churchDataWithFile) => {
    const { logoFile, ...restOfChurchData } = churchDataWithFile;
    setLoading(true);
  
    try {
      // --- UPDATE LOGIC ---
      if (selectedChurch?.id) {
        let finalLogoUrl = restOfChurchData.logoUrl || '';
  
        if (logoFile) {
          const logoPath = `churches/${selectedChurch.id}/logos/${logoFile.name}`;
          finalLogoUrl = await uploadFile(logoFile, logoPath);
  
          // Delete old logo if a new one is uploaded
          if (selectedChurch.logoUrl && selectedChurch.logoUrl !== finalLogoUrl) {
              await deleteFileByUrl(selectedChurch.logoUrl);
          }
        } else if (restOfChurchData.logoUrl === '' && selectedChurch.logoUrl) {
          // Handle case where logo is removed without uploading a new one
          await deleteFileByUrl(selectedChurch.logoUrl);
        }
        
        const churchDataToUpdate = { ...restOfChurchData, logoUrl: finalLogoUrl };
        await updateDoc(doc(db, 'churches', selectedChurch.id), churchDataToUpdate);
        alert("Church updated successfully!");

      // --- CREATE LOGIC ---
      } else {
        if (!isSuperAdmin) {
          throw new Error("Permission denied: Only Super Administrators can create new churches.");
        }

        // 1. Call the cloud function to create the church and secret
        const functions = getFunctions();
        const createChurch = httpsCallable(functions, 'createChurch');
        const result = await createChurch({ churchName: restOfChurchData.name });
        const newChurchId = result.data.churchId;

        let finalLogoUrl = '';
        const dataToUpdate = { ...restOfChurchData };
        delete dataToUpdate.name; // Name is already set by the cloud function

        // 2. If there's a logo, upload it now that we have the ID
        if (logoFile) {
          const logoPath = `churches/${newChurchId}/logos/${logoFile.name}`;
          finalLogoUrl = await uploadFile(logoFile, logoPath);
          dataToUpdate.logoUrl = finalLogoUrl;
        }

        // 3. Update the new church doc with the rest of the form data (logo, timezone, etc.)
        if (Object.keys(dataToUpdate).length > 0) {
            await updateDoc(doc(db, 'churches', newChurchId), dataToUpdate);
        }

        alert("Church created successfully!");
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error saving church:", error);
      alert("Failed to save church: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!isSuperAdmin || !selectedChurch) return;
    setLoading(true);

    try {
      if (selectedChurch.logoUrl) {
        await deleteFileByUrl(selectedChurch.logoUrl);
      }
      await deleteDoc(doc(db, 'churches', selectedChurch.id));
      alert("Church deleted successfully!");
      handleCloseConfirm();
      setSelectedChurch(null);
    } catch (error) {
      console.error("Error deleting church:", error);
      alert("Failed to delete church: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChurchLogo = async (churchId, logoUrlToDelete) => {
    if (!churchId || !logoUrlToDelete) return;
    try {
      await deleteFileByUrl(logoUrlToDelete);
      await updateDoc(doc(db, 'churches', churchId), { logoUrl: '' });
    } catch (error) {
      console.error("Error removing logo:", error);
      alert("Failed to remove logo.");
    }
  };

  if (loading && churches.length === 0) return <CircularProgress />;

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom>Church Management</Typography>
        {isSuperAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateChurch}>
            Create New Church
          </Button>
        )}
      </Box>

      <Paper elevation={3} sx={{ p: 2 }}>
        <List>
          {churches.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: 'center' }}>
              {isChurchAdmin && userProfile?.churchId ? "No information available for your church." : "No churches found."}
            </Typography>
          ) : (
            churches.map((church, i) => (
              <React.Fragment key={church.id}>
                <ListItem>
                  <ListItemText primary={church.name} secondary={`ID: ${church.id}`} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button startIcon={<EditIcon />} onClick={() => handleEditChurch(church)} sx={{ mr: 1 }}>
                      Edit
                    </Button>
                    <Button
                      startIcon={<AttachMoneyIcon />}
                      onClick={() => handleOpenGivingInfoModal(church)}
                      sx={{ mr: 1 }}
                    >
                      Giving Info
                    </Button>
                    {isSuperAdmin && (
                      <>
                        <Button
                          startIcon={<CleaningServicesIcon />}
                          color="warning"
                          onClick={() => handleWipeTokensRequest(church)}
                          sx={{ mr: 1 }}
                        >
                          Wipe Tokens
                        </Button>
                        <Button
                          startIcon={<MeetingRoomIcon />}
                          color="error"
                          onClick={() => handleForceReloginRequest(church)}
                          sx={{ mr: 1 }}
                        >
                          Force Re-login
                        </Button>
                        <Button
                          startIcon={<FolderDeleteIcon />}
                          color="error"
                          onClick={() => handleWipeAnnouncementsRequest(church)}
                          sx={{ mr: 1 }}
                        >
                          Wipe Announcements
                        </Button>
                        <Button
                          startIcon={<NotificationsOffIcon />}
                          color="error"
                          onClick={() => handleWipeNotificationsRequest(church)}
                          sx={{ mr: 1 }}
                        >
                          Wipe User Notifications
                        </Button>
                        <Button startIcon={<DeleteIcon />} color="error" onClick={() => handleDeleteRequest(church)}>
                          Delete Church
                        </Button>
                      </>
                    )}
                  </Box>
                </ListItem>
                {i < churches.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>

      <ChurchEditModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveChurch}
        church={selectedChurch}
        initialLogoUrl={selectedChurch?.logoUrl}
        onDeleteLogo={handleDeleteChurchLogo}
        editorProfile={userProfile}
      />
      
      {selectedChurch && (
        <GivingInfoEditModal
          open={isGivingInfoModalOpen}
          onClose={handleCloseGivingInfoModal}
          churchId={selectedChurch.id}
          givingMethods={givingMethods}
        />
      )}

      <Dialog open={isConfirmOpen} onClose={handleCloseConfirm}>
        <DialogTitle>Delete Church?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete the church "{selectedChurch?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">Confirm Delete</Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={isWipeConfirmOpen} onClose={() => setIsWipeConfirmOpen(false)}>
        <DialogTitle>Wipe Notification Tokens?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all notification tokens for every member of **{churchToWipe?.name}**?
            This will stop them from receiving push notifications until they log in again. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsWipeConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmWipeTokens} color="warning">Confirm Wipe</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isForceReloginConfirmOpen} onClose={() => setIsForceReloginConfirmOpen(false)}>
        <DialogTitle>Force Re-login for Church Members?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to force all members of **{churchToForceRelogin?.name}** to re-login?
            This will invalidate all their active sessions and require them to enter their credentials again. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsForceReloginConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmForceRelogin} color="error">Confirm Force Re-login</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isWipeAnnouncementsConfirmOpen} onClose={() => setIsWipeAnnouncementsConfirmOpen(false)}>
        <DialogTitle>Wipe All Announcements?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete ALL announcements and their attachments for **{churchToWipeAnnouncements?.name}**?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsWipeAnnouncementsConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmWipeAnnouncements} color="error">Confirm Wipe Announcements</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isWipeNotificationsConfirmOpen} onClose={() => setIsWipeNotificationsConfirmOpen(false)}>
        <DialogTitle>Wipe All Member Notifications?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete ALL in-app notifications for every member of **{churchToWipeNotifications?.name}**?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsWipeNotificationsConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmWipeNotifications} color="error">Confirm Wipe Notifications</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}

export default ChurchManagement;